import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  signInWithCustomToken,
  linkWithCredential,
  GoogleAuthProvider,
} from "firebase/auth";
import { OAuthProvider } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, googleProvider, functions } from "./firebase";
import { clearStoredIdToken, persistIdToken } from "./tokenUtils";

export const waitForAuthReady = () =>
  new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user || null);
    });
  });

export const getFreshIdToken = async (forceRefresh = false) => {
  const user = auth.currentUser || (await waitForAuthReady());
  if (!user) return null;
  const token = await user.getIdToken(forceRefresh);
  if (token) {
    persistIdToken(token);
  }
  return token;
};

export const signInWithGooglePopup = async (loginHint) => {
  if (loginHint) {
    googleProvider.setCustomParameters({ login_hint: loginHint });
  }
  return signInWithPopup(auth, googleProvider);
};

const handleManualWeld = async ({ email, providerId, pendingCred }) => {
  if (!pendingCred?.idToken) return null;
  const weldSSO = httpsCallable(functions, "manual_weld_sso");
  const result = await weldSSO({
    email,
    providerId,
    oauthToken: pendingCred.idToken,
  });
  const customToken = result?.data?.customToken;
  if (!customToken) return null;
  const userCred = await signInWithCustomToken(auth, customToken);
  try {
    await linkWithCredential(userCred.user, pendingCred);
  } catch {
    // Ignore link errors; user is already signed in via custom token.
  }
  return userCred;
};

export const signInWithGooglePopupSmart = async (loginHint) => {
  if (loginHint) {
    googleProvider.setCustomParameters({ login_hint: loginHint });
  }
  try {
    return await signInWithPopup(auth, googleProvider);
  } catch (error) {
    if (error?.code === "auth/account-exists-with-different-credential") {
      const pendingCred = GoogleAuthProvider.credentialFromError(error);
      return handleManualWeld({
        email: error?.customData?.email,
        providerId: "google.com",
        pendingCred,
      });
    }
    throw error;
  }
};

export const signInWithGoogleRedirect = async (loginHint) => {
  if (loginHint) {
    googleProvider.setCustomParameters({ login_hint: loginHint });
  }
  return signInWithRedirect(auth, googleProvider);
};

export const signInWithMicrosoftPopup = async (loginHint) => {
  const provider = new OAuthProvider("microsoft.com");
  if (loginHint) {
    provider.setCustomParameters({ login_hint: loginHint });
  }
  return signInWithPopup(auth, provider);
};

export const signInWithMicrosoftPopupSmart = async (loginHint) => {
  const provider = new OAuthProvider("microsoft.com");
  if (loginHint) {
    provider.setCustomParameters({ login_hint: loginHint });
  }
  try {
    return await signInWithPopup(auth, provider);
  } catch (error) {
    if (error?.code === "auth/account-exists-with-different-credential") {
      const pendingCred = OAuthProvider.credentialFromError(error);
      return handleManualWeld({
        email: error?.customData?.email,
        providerId: "microsoft.com",
        pendingCred,
      });
    }
    throw error;
  }
};

export const signInWithMicrosoftRedirect = async (loginHint) => {
  const provider = new OAuthProvider("microsoft.com");
  if (loginHint) {
    provider.setCustomParameters({ login_hint: loginHint });
  }
  return signInWithRedirect(auth, provider);
};

export const exchangeSsoSession = async () => {
  const response = await fetch("/api/sso/firebase-token", {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) return null;
  const { token } = await response.json();
  if (!token) return null;
  const userCredential = await signInWithCustomToken(auth, token);
  return userCredential.user;
};

export const handleGoogleRedirectResult = () => getRedirectResult(auth);

export const firebaseSignOut = async () => {
  await signOut(auth);
  clearStoredIdToken();
};
